import { ACTIVE_LOCALES, LOCALE_META } from "@iec-id/shared/constants";
import { localeEndonym, localeShortLabel, localesForPicker } from "@iec-id/shared/i18n";
import { cn, Icon, Input, Popover, PopoverClose, PopoverItem } from "@iec-id/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/lib/locale";

/**
 * Смена языка на экранах входа (макет 4a, шапка карточки).
 *
 * Стоит именно здесь, а не в профиле: до входа профиля ещё нет, а прочитать
 * «Введите код из письма» человек должен на своём языке — иначе переключить
 * язык он сможет только после того, как разберётся в чужом.
 *
 * Форма повторяет переключатель экосистемы (StudyHub `layout/locale-switcher`,
 * Hero `layout/language-switcher`): глобус + короткий ярлык в триггере, в меню —
 * эндоним с галочкой, порядок `en` → ISO A→Z, высота ограничена скроллом.
 *
 * Строка поиска появляется, только когда список перестаёт читаться целиком
 * (`SEARCH_THRESHOLD`). С 21 активной локалью (Д11) она показывается всегда;
 * порог оставлен, потому что он же закрывает обратный случай — на двух языках
 * поле поиска было бы издевательством над человеком, выбирающим из двух.
 *
 * Выбор пишется в `localStorage` детектором i18next (`iec-id.locale`), поэтому
 * он переживает редирект на Google и обратно. В профиль он попадёт на шаге
 * онбординга — отдельного запроса отсюда не делаем: незалогиненному человеку
 * профиль писать некуда.
 */
const SEARCH_THRESHOLD = 8;

/**
 * Есть ли у человека настоящая клавиатура. `matchMedia` может отсутствовать
 * (happy-dom в тестах) — тогда считаем, что есть: на десктопе автофокус в
 * поиске и есть смысл контрола.
 */
function hasHardwareKeyboard(): boolean {
	if (typeof window === "undefined" || !window.matchMedia) return true;
	return window.matchMedia("(pointer: fine)").matches;
}

/** `Intl.DisplayNames` бросает на неизвестной локали — поиск не должен падать. */
function displayNamesFor(locale: string): Intl.DisplayNames | null {
	try {
		return new Intl.DisplayNames([locale], { type: "language" });
	} catch {
		return null;
	}
}

export function LocaleSwitcher({ className }: { className?: string }) {
	const { t, i18n } = useTranslation("signin");
	const current = useLocale();
	const [query, setQuery] = useState("");
	// Считаем один раз на монтирование: тип указателя не меняется по ходу
	// открытого меню, а подписка на media query ради этого — лишний слушатель.
	const [hasKeyboard] = useState(hasHardwareKeyboard);

	const codes = useMemo(() => localesForPicker(ACTIVE_LOCALES), []);

	// Показываем эндоним, а ищем по четырём написаниям сразу: эндоним, код,
	// английское имя и имя на языке текущего интерфейса. Иначе найти язык может
	// только тот, кто уже умеет на нём писать: «gr» не совпадает с «Ελληνικά»,
	// «нем» — с «Deutsch», и человек уходит с ощущением, что языка нет.
	const options = useMemo(() => {
		const display = displayNamesFor(current);
		return codes.map((code) => {
			const meta = (LOCALE_META as Record<string, { englishName: string } | undefined>)[code];
			return {
				code,
				name: localeEndonym(code),
				haystack: [code, localeEndonym(code), meta?.englishName ?? "", display?.of(code) ?? ""]
					.join(" ")
					.toLocaleLowerCase(),
			};
		});
	}, [codes, current]);

	const needle = query.trim().toLocaleLowerCase();
	const visible = needle ? options.filter((option) => option.haystack.includes(needle)) : options;

	// На одной активной локали переключатель — мёртвый элемент управления.
	if (options.length < 2) return null;

	const searchable = options.length > SEARCH_THRESHOLD;

	return (
		<Popover
			align="end"
			width={220}
			contentLabel={t("locale.label")}
			onOpenChange={(open) => {
				if (!open) setQuery("");
			}}
			trigger={
				<button
					type="button"
					aria-label={t("locale.label")}
					className={cn(
						// 36px по макету — меньше мобильного минимума в 44px, поэтому
						// невидимая зона касания из кита. Визуал не меняется.
						"tap-target flex h-9 items-center gap-1.5 rounded-control px-2.5 text-caps-sm text-fg-muted",
						"transition-colors duration-150 ease-smooth hover:bg-sunken hover:text-fg",
						"focus-ring",
						className,
					)}
				>
					<Icon name="Globe" size={16} />
					<span>{localeShortLabel(current)}</span>
					<Icon name="ChevronDown" size={16} />
				</button>
			}
		>
			{searchable ? (
				<div className="p-1 pb-0.5">
					<Input
						size="sm"
						type="search"
						// Автофокус только там, где есть физическая клавиатура. На
						// телефоне он поднимает экранную, а она закрывает сам список,
						// ради которого меню открыли: iOS не уменьшает вьюпорт под
						// клавиатуру, поэтому Radix об этом даже не узнаёт.
						autoFocus={hasKeyboard}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder={t("locale.search")}
						aria-label={t("locale.search")}
						iconBefore={<Icon name="Search" size={16} />}
					/>
				</div>
			) : null}

			{/* Потолок высоты, а не полный список: 21 язык разворачивает поповер
			    на весь экран и прячет карточку входа под собой. Второй предел —
			    сколько места реально осталось до края экрана (минус строка поиска
			    над списком): в коротком вьюпорте фиксированные 280px обрезаются
			    молча, и верх списка уезжает под адресную строку браузера. */}
			<div className="iec-scroll flex max-h-[min(280px,calc(var(--radix-popover-content-available-height)-3.5rem))] flex-col gap-0.5">
				{visible.map((option, index) => (
					<PopoverClose asChild key={option.code}>
						<PopoverItem
							index={index}
							lang={option.code}
							// Список живёт в flex-колонке с потолком высоты, а flex-элементы
							// по умолчанию сжимаются: без этого 21 строка ужимается вдвое
							// и меню перестаёт быть кликабельным (проверено скриншотом).
							className="shrink-0"
							aria-current={option.code === current ? "true" : undefined}
							trailing={
								option.code === current ? (
									<span className="text-brand-text">
										<Icon name="Check" size={16} />
									</span>
								) : undefined
							}
							onClick={() => {
								void i18n.changeLanguage(option.code);
							}}
						>
							{option.name}
						</PopoverItem>
					</PopoverClose>
				))}
				{visible.length === 0 ? (
					<p className="px-2.5 py-3 t-meta text-fg-muted">{t("locale.empty")}</p>
				) : null}
			</div>
		</Popover>
	);
}
